/**
 * Conversation History Firestore Types
 *
 * Type definitions for chat messages stored in Firestore at:
 * users/{uid}/user_history/user_history.conversation_history
 *
 * Uses snake_case for Firestore, camelCase for client.
 */

import { withoutDocSourceAttachments } from '../utils/docSourceAttachments';
import { Timestamp } from 'firebase/firestore';
import type { FieldValue } from 'firebase/firestore';
import type { AIProvider } from './walletFirestore';
import type {
  ChatMessage,
  TokenUsageSnapshot,
  SerializedAttachment,
  SpecialMessageActionButton,
  SpecialMessageType,
  SpecialMessagePriority,
  WorkflowOutputMetadata,
  TokenCostSnapshot,
} from '../components/GlobalChatSystem/types';

// ============================================================================
// Constants
// ============================================================================

export const MAX_CONVERSATION_HISTORY_LENGTH = 25;

// Firestore documents are capped at 1 MiB. Deep Research reports can exceed
// that by themselves, especially when both text and rendered HTML are stored.
// Keep synced history bounded and rely on Storage-backed PDF/DOCX attachments
// for the complete report across devices.
const MAX_FIRESTORE_MESSAGE_TEXT_CHARS = 20000;
const MAX_FIRESTORE_MESSAGE_HTML_CHARS = 30000;
const MAX_FIRESTORE_WORKFLOW_CONTENT_CHARS = 20000;
// copy: storage_truncation_notice
const FIRESTORE_TRUNCATION_NOTICE =
  '\n\n[Synced history note: Full content was too large for Firestore chat history. Use the attached PDF/DOCX downloads for the complete report.]';

// ============================================================================
// Firestore Document Types (snake_case)
// ============================================================================

/**
 * Token cost snapshot stored in Firestore
 */
export interface TokenCostSnapshotDoc {
  input_cost?: number;
  output_cost?: number;
  reasoning_cost?: number;
  citation_cost?: number;
  search_cost?: number;
  total_cost?: number;
  model?: string;
  provider?: string;
}

/**
 * Token usage snapshot stored in Firestore
 */
export interface TokenUsageSnapshotDoc {
  input_tokens?: number;
  output_tokens?: number;
  reasoning_tokens?: number;
  citation_tokens?: number;
  search_queries?: number;
  total_tokens?: number;
  model?: string;
  provider?: AIProvider;
  response_time_ms?: number;
  timestamp_iso?: string;
  summary?: string;
  /** Authoritative upstream cost (USD, pre-5X) — without it, cross-device
   * reloads recompute from token counts and show less than the wallet debit. */
  upstream_cost_usd?: number;
  cost?: TokenCostSnapshotDoc;
  /** 2026-09-08: lane / stage / cost-source flags survive a reload — before, a vendor-billed
   *  media step lost its "billed to vendor" label and an estimate its "≈" the moment the thread
   *  re-hydrated from Firestore. */
  billed_separately?: boolean;
  stage?: string;
  cost_source?: 'provider' | 'estimate';
}

/**
 * Serialized attachment metadata in Firestore
 * NOTE: dataUrl is intentionally omitted - too large for Firestore (1MB doc limit)
 * Firebase Storage URL is stored instead for cross-device persistence.
 */
export interface SerializedAttachmentDoc {
  name: string;
  type: string;
  size: number;
  last_modified?: number;
  /** Firebase Storage download URL - enables cross-device attachment access */
  storage_url?: string;
  /** Firebase Storage path for reference/deletion */
  storage_path?: string;
}

/**
 * Workflow output metadata in Firestore
 */
export interface WorkflowOutputMetadataDoc {
  type: 'research_report' | 'formatted_content' | 'video_script' | 'translation';
  content: string;
  title?: string;
  timestamp: Timestamp;
  source_workflow: string;
}

/**
 * Action button in Firestore
 */
export interface ActionButtonDoc {
  button_text: string;
  action_type: string;
  action_value: string;
  button_style: string;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Single chat message stored in Firestore
 */
export interface ChatMessageDoc {
  text: string;
  is_user: boolean;
  timestamp: Timestamp;
  html?: string;
  source_tag?: string;
  /** P3 (2026-09-16): the message was posted by the platform with no model
   *  step — the ⓘ names which sweep instead of only saying no compute was
   *  needed. Snake_case on the wire like every other field here. */
  platform_note?: { sourceTag?: string; postedAtMs?: number };
  // Token usage (single model)
  token_usage_snapshot?: TokenUsageSnapshotDoc;
  // Token usages (multi-model)
  token_usage_snapshots?: TokenUsageSnapshotDoc[];
  // Attachments metadata (no dataUrl - too large for Firestore)
  attachments_metadata?: SerializedAttachmentDoc[];
  // Special message properties
  message_type?: SpecialMessageType;
  priority?: SpecialMessagePriority;
  title?: string;
  action_buttons?: ActionButtonDoc[];
  // Workflow output
  workflow_output?: WorkflowOutputMetadataDoc;
  workflow_action_buttons_html?: string;
  /** Index of selected action button (for persisting user's button choice) */
  selected_action_button_index?: number;
  /** Durable task ID for resuming long-running server operations on rehydrate */
  server_task_id?: string;
  /** If true, message is included in context for AI but hidden from UI */
  is_context_only?: boolean;
  /** Tags for categorization (e.g., "test example" for test data) */
  tags?: string[];
  /** Structured data for data-only cards (connect offers etc., 2026-08-22) —
   *  the reload dedupe guard and card rehydration read it. */
  payload?: Record<string, unknown>;
}

/**
 * User history document structure in Firestore
 * Path: users/{uid}/user_history/user_history
 */
export interface UserHistoryDoc {
  conversation_history: ChatMessageDoc[];
  last_updated: Timestamp;
  version: number;
}

// ============================================================================
// Helper: Remove undefined values (Firestore doesn't accept undefined)
// ============================================================================

function removeUndefined<T extends Record<string, any>>(obj: T): T {
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result as T;
}

// ============================================================================
// Defensive sanitizer — strips Firestore FieldValue placeholders + undefined
// from anywhere inside a ChatMessageDoc (or array of them) before they hit
// `transaction.set` / `setDoc`.
//
// Why this exists
// ───────────────
// Firestore rejects an entire document write with HTTP 400 +
// `failed-precondition` if a `FieldValue` placeholder (the value returned
// from `serverTimestamp()`, `arrayUnion()`, `arrayRemove()`, `increment()`,
// `deleteField()`) appears INSIDE an array element or nested under any
// transform-incompatible path. The `conversation_history` field is an
// array of `ChatMessageDoc` — so even one stray `FieldValue` deep inside a
// single message will fail the entire commit and freeze the chat sync
// loop on a lowercase "loading..." indicator (Miles's live-demo repro on
// 2026-05-09 at 07:34:08.470Z).
//
// `toChatMessageDoc()` already calls a SHALLOW `removeUndefined()` on the
// fields it builds, but it can't catch a `FieldValue` that arrives via
// `...message` spread, an upstream serializer, or a future field added to
// `ChatMessage` before the serializer is updated. This sanitizer is the
// last line of defence: it walks every leaf of a `ChatMessageDoc` and
// guarantees the shape Firestore will accept.
//
// Behaviour
// ─────────
//   - `undefined`                      → field dropped
//   - `FieldValue` instance (any kind) → field dropped + console.warn
//   - `Date`                           → converted to `Timestamp`
//   - `Timestamp`                      → preserved
//   - primitives / null                → preserved
//   - arrays                           → recursed element-wise
//   - plain objects                    → recursed property-wise
//
// The function name `sanitizeChatMessageDocsForFirestore_v20260509`
// doubles as a build-time grep marker so we can verify the live bundle
// includes the fix without inspecting source maps.
// ============================================================================

/**
 * Detect a Firestore `FieldValue` instance without leaning on instanceof
 * (different bundles can produce different prototype chains in user code
 * vs. the Firestore SDK, so a duck-type check is more reliable).
 */
function isFieldValuePlaceholder(value: unknown): value is FieldValue {
  if (!value || typeof value !== 'object') return false;
  // Firestore's FieldValue subclasses (ServerTimestampFieldValueImpl,
  // ArrayUnionFieldValueImpl, etc.) all expose `_methodName` (modular SDK)
  // and a public `isEqual` method. Their constructor name also ends in
  // `FieldValueImpl` / `FieldValue`. Match liberally.
  const v = value as Record<string, unknown>;
  if (typeof v._methodName === 'string') return true;
  const ctorName = (value as { constructor?: { name?: string } })?.constructor?.name || '';
  if (/FieldValue/i.test(ctorName)) return true;
  return false;
}

function sanitizeForFirestoreInner(value: unknown, path: string, warnings: string[]): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (isFieldValuePlaceholder(value)) {
    warnings.push(path);
    return undefined;
  }
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? Timestamp.fromDate(value) : undefined;
  }
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return value;
  if (Array.isArray(value)) {
    const out: unknown[] = [];
    for (let i = 0; i < value.length; i++) {
      const cleaned = sanitizeForFirestoreInner(value[i], `${path}[${i}]`, warnings);
      if (cleaned !== undefined) out.push(cleaned);
    }
    return out;
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      const cleaned = sanitizeForFirestoreInner(obj[key], `${path}.${key}`, warnings);
      if (cleaned !== undefined) out[key] = cleaned;
    }
    return out;
  }
  // Functions, symbols, bigints, etc. — drop them with a warning.
  warnings.push(path);
  return undefined;
}

/**
 * Sanitize an array of `ChatMessageDoc` objects so it is safe to embed
 * inside `users/{uid}/user_history/user_history.conversation_history`.
 *
 * Returns a new array with all `undefined` and `FieldValue` placeholders
 * stripped recursively. Logs (via console.warn) the dotted paths of any
 * stripped `FieldValue` placeholders so we can hunt down the upstream
 * write site if it ever happens again.
 *
 * NOTE: name is intentionally explicit + dated — it is the live-bundle
 * verification marker for the 2026-05-09 Firestore-400 fix.
 */
export function sanitizeChatMessageDocsForFirestore_v20260509(
  docs: ChatMessageDoc[]
): ChatMessageDoc[] {
  const warnings: string[] = [];
  const sanitized = (sanitizeForFirestoreInner(docs, 'conversation_history', warnings) as ChatMessageDoc[]) || [];
  if (warnings.length > 0) {
    // Cap the noise — one warn line is enough to diagnose.
    const sample = warnings.slice(0, 5).join(', ');
    const more = warnings.length > 5 ? ` (+${warnings.length - 5} more)` : '';
    console.warn(
      `[conversationHistory] Sanitizer stripped ${warnings.length} invalid leaf value(s) ` +
      `(FieldValue/undefined/non-serializable) before Firestore commit: ${sample}${more}`
    );
  }
  return sanitized;
}

function truncateForFirestore(value: string | undefined, maxChars: number): string | undefined {
  if (!value) return value;
  if (value.length <= maxChars) return value;

  const available = Math.max(0, maxChars - FIRESTORE_TRUNCATION_NOTICE.length);
  return `${value.slice(0, available).trimEnd()}${FIRESTORE_TRUNCATION_NOTICE}`;
}

// ============================================================================
// Conversion Functions: Client -> Firestore
// ============================================================================

function toTokenCostSnapshotDoc(
  cost: TokenCostSnapshot | undefined
): TokenCostSnapshotDoc | undefined {
  if (!cost) return undefined;

  return removeUndefined({
    input_cost: cost.inputCost,
    output_cost: cost.outputCost,
    reasoning_cost: cost.reasoningCost,
    citation_cost: cost.citationCost,
    search_cost: cost.searchCost,
    total_cost: cost.totalCost,
    model: cost.model,
    provider: cost.provider,
  });
}

export function toTokenUsageSnapshotDoc(
  snapshot: TokenUsageSnapshot | undefined
): TokenUsageSnapshotDoc | undefined {
  if (!snapshot) return undefined;

  return removeUndefined({
    input_tokens: snapshot.inputTokens,
    output_tokens: snapshot.outputTokens,
    reasoning_tokens: snapshot.reasoningTokens,
    citation_tokens: snapshot.citationTokens,
    search_queries: snapshot.searchQueries,
    total_tokens: snapshot.totalTokens,
    model: snapshot.model,
    provider: snapshot.provider,
    response_time_ms: snapshot.responseTimeMs,
    timestamp_iso: snapshot.timestampIso,
    summary: snapshot.summary,
    upstream_cost_usd: snapshot.upstreamCostUsd,
    cost: toTokenCostSnapshotDoc(snapshot.cost),
    billed_separately: snapshot.billedSeparately === true ? true : undefined,
    stage: snapshot.stage,
    cost_source: snapshot.costSource,
  });
}

export function toSerializedAttachmentDoc(
  att: SerializedAttachment
): SerializedAttachmentDoc {
  return removeUndefined({
    name: att.name,
    type: att.type,
    size: att.size,
    last_modified: att.lastModified,
    // NOTE: dataUrl intentionally omitted - too large for Firestore
    // Firebase Storage URL is stored instead for cross-device persistence
    storage_url: att.storageUrl,
    storage_path: att.storagePath,
  });
}

export function toWorkflowOutputDoc(
  output: WorkflowOutputMetadata | undefined
): WorkflowOutputMetadataDoc | undefined {
  if (!output) return undefined;

  return removeUndefined({
    type: output.type,
    content: truncateForFirestore(output.content, MAX_FIRESTORE_WORKFLOW_CONTENT_CHARS) || '',
    title: output.title,
    timestamp: Timestamp.fromDate(
      output.timestamp instanceof Date ? output.timestamp : new Date(output.timestamp)
    ),
    source_workflow: output.sourceWorkflow,
  });
}

export function toActionButtonDoc(
  btn: SpecialMessageActionButton
): ActionButtonDoc {
  return removeUndefined({
    button_text: btn.buttonText,
    action_type: btn.actionType,
    action_value: btn.actionValue,
    button_style: btn.buttonStyle,
    disabled: btn.disabled,
    loading: btn.loading,
  });
}

/**
 * This store's ONE attachment list for a message: its own files, then the
 * files it carried from storage (a saved message's; one draft bar, step 4b),
 * each once by location. With none carried it is the very list it was, so
 * every other message is written exactly as before.
 *
 * Written out here rather than imported from the chat's stored-file rules:
 * this module rides the first download every visitor pays for (the history
 * provider mounts in App), and those rules belong to the chat engine.
 */
function historyAttachmentsOf(message: ChatMessage): SerializedAttachment[] | undefined {
  const carried = Array.isArray(message.attachmentsStored) ? message.attachmentsStored : [];
  if (carried.length === 0) return message.attachmentsSerialized;
  const out = (message.attachmentsSerialized ?? []).slice();
  const seen = new Set(out.map((att) => att?.storagePath).filter((path): path is string => Boolean(path)));
  for (const att of carried) {
    const path = att?.storagePath;
    if (typeof path !== 'string' || !path || seen.has(path)) continue;
    seen.add(path);
    out.push(att);
  }
  return out;
}

export function toChatMessageDoc(message: ChatMessage): ChatMessageDoc {
  const timestamp =
    message.timestamp instanceof Date
      ? message.timestamp
      : new Date(message.timestamp);

  const text = truncateForFirestore(message.text || '', MAX_FIRESTORE_MESSAGE_TEXT_CHARS) || '';
  const html = (() => {
    if (!message.html) return undefined;

    // Research reports are already represented by markdown-ish text plus
    // Storage-backed attachments. Persisting generated HTML duplicates the
    // large report and can make the whole history doc unwritable.
    if (message.sourceTag === 'research_result' && message.html.length > MAX_FIRESTORE_MESSAGE_HTML_CHARS) {
      return undefined;
    }

    return truncateForFirestore(message.html, MAX_FIRESTORE_MESSAGE_HTML_CHARS);
  })();

  // Cross-view ⓘ fix (Miles 2026-07-12): callers may hand this mapper
  // in-memory messages whose usage still lives only in the RAW
  // tokenUsage/tokenUsages fields (the snapshot conversion is throttled) —
  // snapshot-only reads silently dropped usage forever. Fall back to raw.
  const usageSnapshotSource = message.tokenUsageSnapshot
    ?? (message.tokenUsage
      ? {
        ...message.tokenUsage,
        timestampIso: message.tokenUsage.timestamp instanceof Date
          ? message.tokenUsage.timestamp.toISOString()
          : undefined,
      } as unknown as TokenUsageSnapshot
      : undefined);
  const usageSnapshotsSource = (message.tokenUsageSnapshots && message.tokenUsageSnapshots.length > 0)
    ? message.tokenUsageSnapshots
    : (Array.isArray(message.tokenUsages) && message.tokenUsages.length > 0
      ? message.tokenUsages.map((u) => ({
        ...u,
        timestampIso: u.timestamp instanceof Date ? u.timestamp.toISOString() : undefined,
      }) as unknown as TokenUsageSnapshot)
      : undefined);

  // Build object and remove undefined values (Firestore rejects undefined)
  // Sub-functions already apply removeUndefined internally
  return removeUndefined({
    text,
    is_user: message.isUser,
    timestamp: Timestamp.fromDate(timestamp),
    html,
    source_tag: message.sourceTag,
    // Normalized on the way out: the message type allows nulls (a row that has
    // none), the wire doc does not — an explicit undefined is dropped by
    // removeUndefined, so nothing writes an empty object into history.
    platform_note: message.platformNote
      ? {
          ...(message.platformNote.sourceTag ? { sourceTag: message.platformNote.sourceTag } : {}),
          ...(typeof message.platformNote.postedAtMs === 'number' ? { postedAtMs: message.platformNote.postedAtMs } : {}),
        }
      : undefined,
    token_usage_snapshot: toTokenUsageSnapshotDoc(usageSnapshotSource),
    token_usage_snapshots: usageSnapshotsSource
      ?.map(toTokenUsageSnapshotDoc)
      .filter((s): s is TokenUsageSnapshotDoc => s !== undefined),
    attachments_metadata: historyAttachmentsOf(message)?.map(toSerializedAttachmentDoc),
    message_type: message.messageType,
    priority: message.priority,
    title: message.title,
    action_buttons: message.actionButtons?.map(toActionButtonDoc),
    workflow_output: toWorkflowOutputDoc(message.workflowOutput),
    workflow_action_buttons_html: message.workflowActionButtonsHtml,
    selected_action_button_index: message.selectedActionButtonIndex,
    server_task_id: message.serverTaskId,
    is_context_only: message.isContextOnly,
    tags: message.tags,
    payload: message.payload as Record<string, unknown> | undefined,
  });
}

// ============================================================================
// Conversion Functions: Firestore -> Client
// ============================================================================

function fromTokenCostSnapshotDoc(
  doc: TokenCostSnapshotDoc | undefined
): TokenCostSnapshot | undefined {
  if (!doc) return undefined;

  return {
    inputCost: doc.input_cost,
    outputCost: doc.output_cost,
    reasoningCost: doc.reasoning_cost,
    citationCost: doc.citation_cost,
    searchCost: doc.search_cost,
    totalCost: doc.total_cost,
    model: doc.model,
    provider: doc.provider,
  };
}

export function fromTokenUsageSnapshotDoc(
  doc: TokenUsageSnapshotDoc | undefined
): TokenUsageSnapshot | undefined {
  if (!doc) return undefined;

  return {
    inputTokens: doc.input_tokens,
    outputTokens: doc.output_tokens,
    reasoningTokens: doc.reasoning_tokens,
    citationTokens: doc.citation_tokens,
    searchQueries: doc.search_queries,
    totalTokens: doc.total_tokens,
    model: doc.model,
    provider: doc.provider,
    responseTimeMs: doc.response_time_ms,
    timestampIso: doc.timestamp_iso,
    summary: doc.summary,
    upstreamCostUsd: doc.upstream_cost_usd,
    cost: fromTokenCostSnapshotDoc(doc.cost),
    ...(doc.billed_separately === true ? { billedSeparately: true } : {}),
    ...(typeof doc.stage === 'string' && doc.stage ? { stage: doc.stage } : {}),
    ...(doc.cost_source === 'provider' || doc.cost_source === 'estimate' ? { costSource: doc.cost_source } : {}),
  };
}

export function fromSerializedAttachmentDoc(
  doc: SerializedAttachmentDoc
): SerializedAttachment {
  return {
    name: doc.name,
    type: doc.type,
    size: doc.size,
    lastModified: doc.last_modified,
    // dataUrl not stored in Firestore - will be undefined
    // Firebase Storage URL enables cross-device attachment downloads
    storageUrl: doc.storage_url,
    storagePath: doc.storage_path,
  };
}

export function fromWorkflowOutputDoc(
  doc: WorkflowOutputMetadataDoc | undefined
): WorkflowOutputMetadata | undefined {
  if (!doc) return undefined;

  return {
    type: doc.type,
    content: doc.content,
    title: doc.title,
    timestamp: doc.timestamp.toDate(),
    sourceWorkflow: doc.source_workflow,
  };
}

export function fromActionButtonDoc(
  doc: ActionButtonDoc
): SpecialMessageActionButton {
  return {
    buttonText: doc.button_text,
    actionType: doc.action_type as SpecialMessageActionButton['actionType'],
    actionValue: doc.action_value,
    buttonStyle: doc.button_style as SpecialMessageActionButton['buttonStyle'],
    disabled: doc.disabled,
    loading: doc.loading,
  };
}

export function fromChatMessageDoc(doc: ChatMessageDoc): ChatMessage {
  return {
    text: doc.text || '',
    isUser: doc.is_user,
    timestamp: doc.timestamp.toDate(),
    html: doc.html,
    sourceTag: doc.source_tag,
    platformNote: doc.platform_note,
    tokenUsageSnapshot: fromTokenUsageSnapshotDoc(doc.token_usage_snapshot),
    tokenUsageSnapshots: doc.token_usage_snapshots
      ?.map(fromTokenUsageSnapshotDoc)
      .filter((s): s is TokenUsageSnapshot => s !== undefined),
    // Never draw the canonical-source sibling (see docSourceAttachments).
    attachmentsSerialized: withoutDocSourceAttachments(
      doc.attachments_metadata?.map(fromSerializedAttachmentDoc),
    ),
    messageType: doc.message_type,
    priority: doc.priority,
    title: doc.title,
    actionButtons: doc.action_buttons?.map(fromActionButtonDoc),
    workflowOutput: fromWorkflowOutputDoc(doc.workflow_output),
    workflowActionButtonsHtml: doc.workflow_action_buttons_html,
    selectedActionButtonIndex: doc.selected_action_button_index,
    serverTaskId: doc.server_task_id,
    isContextOnly: doc.is_context_only,
    tags: doc.tags,
    payload: doc.payload as ChatMessage['payload'],
  };
}
